import { keepPreviousData, useInfiniteQuery, useQuery } from '@tanstack/react-query'
import { useCallback, useEffect, useRef, useState } from 'react'
import type { SearchResult, Sort } from './api'
import { api } from './api'
import type { Group } from './tree'
import { asRoot, emptyTree, toWire } from './tree'

export type View = 'grid' | 'list' | 'timeline'
export const PAGE = 48

/** Search state, mirrored into the URL (?q=<formula>&sort=&view=) so searches can be shared. */
export function useSearchState() {
  const params = new URLSearchParams(window.location.search)
  const [tree, setTreeState] = useState<Group>(emptyTree)
  const [sort, setSort] = useState<Sort>((params.get('sort') as Sort) || 'relevance')
  const [view, setView] = useState<View>((params.get('view') as View) || 'grid')
  const [ready, setReady] = useState(!params.get('q'))
  const [initError, setInitError] = useState<string | null>(null)

  useEffect(() => {
    const q = new URLSearchParams(window.location.search).get('q')
    if (!q) return
    api.parse(q)
      .then((r) => setTreeState(asRoot(r.query)))
      .catch((e) => setInitError(`Die Suche aus dem Link konnte nicht gelesen werden: ${e.message}`))
      .finally(() => setReady(true))
  }, [])

  const setTree = useCallback((t: Group | ((prev: Group) => Group)) => setTreeState((prev) => asRoot(typeof t === 'function' ? t(prev) : t)), [setTreeState])

  return { tree, setTree, sort, setSort, view, setView, ready, initError }
}

export function syncUrl(text: string, sort: Sort, view: View) {
  const p = new URLSearchParams()
  if (text) p.set('q', text)
  if (sort !== 'relevance') p.set('sort', sort)
  if (view !== 'grid') p.set('view', view)
  const qs = p.toString()
  const url = window.location.pathname + (qs ? `?${qs}` : '')
  if (url !== window.location.pathname + window.location.search) window.history.replaceState(null, '', url)
}

/** Debounce a value; used so typing in the formula doesn't fire a search per keystroke. */
export function useDebounced<T>(value: T, ms: number): T {
  const [v, setV] = useState(value)
  const first = useRef(true)
  useEffect(() => {
    if (first.current) { first.current = false; setV(value); return }
    const t = setTimeout(() => setV(value), ms)
    return () => clearTimeout(t)
  }, [value, ms])
  return v
}

export function useResults(tree: Group, sort: Sort, enabled: boolean) {
  const wire = JSON.stringify(toWire(tree))
  return useInfiniteQuery<SearchResult>({
    queryKey: ['search', wire, sort],
    enabled,
    initialPageParam: 0,
    queryFn: ({ pageParam, signal }) =>
      api.search(tree, { sort, offset: pageParam as number, limit: PAGE, facets: pageParam === 0 }, signal),
    getNextPageParam: (last) => (last.offset + last.hits.length < Math.min(last.total, 9800) ? last.offset + last.hits.length : undefined),
    placeholderData: keepPreviousData,
    staleTime: 60_000,
    retry: (n, err) => n < 1 && (err as { status?: number }).status !== 400,
  })
}

export function useMe() {
  return useQuery({ queryKey: ['me'], queryFn: api.me, staleTime: 5 * 60_000 })
}

// --- theme --------------------------------------------------------------------

export function useTheme() {
  const initial = (): 'light' | 'dark' => {
    try {
      const s = localStorage.getItem('zint-theme')
      if (s === 'light' || s === 'dark') return s
    } catch { /* storage unavailable */ }
    return window.matchMedia('(prefers-color-scheme: dark)').matches ? 'dark' : 'light'
  }
  const [theme, setTheme] = useState<'light' | 'dark'>(initial)
  useEffect(() => {
    document.documentElement.setAttribute('data-theme', theme)
    try { localStorage.setItem('zint-theme', theme) } catch { /* ignore */ }
  }, [theme])
  return [theme, setTheme] as const
}

export function useStored<T>(key: string, initial: T) {
  const [v, setV] = useState<T>(() => {
    try {
      const s = localStorage.getItem(key)
      return s ? (JSON.parse(s) as T) : initial
    } catch {
      return initial
    }
  })
  useEffect(() => {
    try { localStorage.setItem(key, JSON.stringify(v)) } catch { /* ignore */ }
  }, [key, v])
  return [v, setV] as const
}
